import { type CSSProperties, useContext, useMemo } from 'react';

import { OverlayStackingContext } from '../contexts/OverlayStacking.js';

/**
 * Type defining the allowed elevation levels in the Strato Design System.
 * They are used to layer UI components.
 * @public
 */
export type Elevation =
  | 'default'
  | 'dropdown'
  | 'sticky'
  | 'overlay'
  | 'modal-backdrop'
  | 'feature-highlight'
  | 'microguide';

// Should eventually probably be linked to the design-tokens. But the elevations
// in the tokens are still WIP and not well defined.
const ElevationMapping: Record<Elevation, number> = {
  default: 1,
  dropdown: 10,
  sticky: 20,
  overlay: 30,
  'modal-backdrop': 40,
  'feature-highlight': 50,
  microguide: 60,
};

/**
 * To stack overlays correctly on top of each other, use the `useOverlayStacking`
 * hook. The hook returns the styles you need to ensure a correct stacking order.
 * Applying the returned styles to the container, its position will be fixed.
 * @public
 */
export function useOverlayStacking(
  topOffset: number | undefined = 0,
  additionalElevationToken?: Elevation,
) {
  const overlayStacking = useContext(OverlayStackingContext);

  const additionalElevation = additionalElevationToken
    ? ElevationMapping[additionalElevationToken]
    : undefined;

  const overlayContainerProps = useMemo<{ style: CSSProperties }>(() => {
    return {
      style: {
        position: 'fixed',
        top: topOffset,
        left: 0,
        zIndex:
          overlayStacking > 0
            ? `calc(${overlayStacking * 100 + (additionalElevation ?? 0)})`
            : additionalElevation,
      } as CSSProperties,
    };
  }, [topOffset, overlayStacking, additionalElevation]);

  return overlayContainerProps;
}
